import React, { useEffect, useState } from "react";
import { Button, Card, Spin, Steps, Typography } from "antd";
import { useForm } from "antd/lib/form/Form";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import { labelConfig, staticConfig, apiConfig } from "./securityProduct.config";
import { BOM } from "../../../services/definedMethod";
import cnt from "../../../services/definedConstant";
import { useLocation } from "react-router-dom";

import ProductIdentityForm from "./components/ProductIdentityForm";
import EconomicBehaviour from "./components/EconomicBehaviour";
import PricingYieldConfigurationForm from "./components/PricingYieldConfigurationForm";
import AccountingIFRSForm from "./components/AccountingIFRSForm";
import RiskRegulatoryBehaviourForm from "./components/RiskRegulatoryBehaviourForm";
import LifecycleEventControlsForm from "./components/LifecycleEventControls";
import LimitsEligibilityRules from "./components/LimitsEligibilityRules";
import DayCountCalendarRulesForm from "./components/DayCountCalendarRulesForm";
import SecurityProductPreview from "./components/Preview";
import { showCheckerApprovalModal } from "../../../components/CheckerModal";
import { accountStatus } from "../../../components/facilities/AccountFacility";

const { Text } = Typography;

const mapLov = (list = []) =>
  list.map((item) => ({
    value: item.value,
    label: item.text,
    code: item.code,
  }));

const valueLovMap = (list = []) =>
  list.map((item) => ({
    value: item.id,
    label: item.name,
    code: item.code,
  }));

const yesNoOptions = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

const maturityAdjustmentRule = [
  { label: "Adjust on Holiday", value: true },
  { label: "Do Not Adjust", value: false },
];

const AddSecurityProduct = ({ userData }) => {
  const { session } = userData;
  const navigate = useNavigate();
  const [form] = useForm();
  const [current, setCurrent] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [filteredInstrumentTypeOptions, setFilteredInstrumentTypeOptions] =
    useState([]);
  const { state: selectedProductId } = useLocation();
  const isEditMode = Boolean(selectedProductId);

  const [lov, setLov] = useState({
    productCategoryOptions: [],
    instrumentTypeOptions: [],
    assetClassOptions: [],
    issuerTypeOptions: [],
    currencyOptions: [],
    dayCountConventionOptions: [],
    couponFrequencyOptions: [],
    businessDayConventionOptions: [],
    quotationTypeOptions: [],
    marketTypeOptions: [],
    pricingMethods: [],
    valuationApproachOptions: [],
    productCodeOptions: [],
    cleanDirtyPriceFlagOptions: [],
    valuationCurveTypeOptions: [],
    yieldBasisOptions: [],
    businessDayAdjustmentOptions: [],
    holidayCalendarOptions: [],
    settlementDateTypes: [],
    ifrsClassificationOptions: [],
    incomeGlAccounts: [],
    assetGLAccounts: [],
    durationMethodOptions: [],
    exposureTypeOptions: [],
    maturityEventCodeOptions: [],
    currencies: [],
    accountingBasisOptions: [],
    bondCouponFrequencies: [],
    interestCalculationBasisOptions: [],
    creditRiskClassOptions: [],
  });

  const [productStatusOptions] = useState([
    { value: "ACTIVE", label: "Active" },
    { value: "SUSPENDED", label: "Suspended" },
    { value: "CLOSED", label: "Closed" },
  ]);

  // Add this mapping near your yesNoOptions constant
  const categoryInstrumentMap = {
    0: ["Simple Interest"], // Discount
    1: ["Fixed Coupon", "Floating"], // Fixed Income
    2: ["Simple Interest"], // Money Market
  };

  const [formData, setFormData] = useState({
    productIdentity: {},
    behaviourRules: {},
    pricingLogic: {},
    dayCountCalendarRules: {},
    accountingIFRS: {},
    riskRegulatoryBehaviour: {},
    lifeCycleAndEvents: {},
    limitsEligibilityRules: {},
  });

  const stepTitles = [
    "Product Identity & Classification",
    "Economic Behaviour & Cashflow Rules",
    "Pricing & Yield Configuration",
    "Day Count & Calendar Rules",
    "Accounting & IFRS Behaviours",
    "Risk & Regulatory Behaviours",
    "Lifecycle & Event Controls",
    "Limits & Eligibility Rules",
    "Preview",
  ];

  useEffect(() => {
    getSecurityProductTemplate();
    if (isEditMode) fetchProductById(selectedProductId);
  }, []);

  useEffect(() => {
    if (
      formData.productIdentity?.productCategory !== undefined && 
      formData.productIdentity?.productCategory !== null &&
      lov.instrumentTypeOptions.length > 0
    ) {
      const allowedCodes =
        categoryInstrumentMap[formData.productIdentity.productCategory] || [];
      const filtered = lov.instrumentTypeOptions.filter((opt) =>
        allowedCodes.includes(opt.code),
      );
      setFilteredInstrumentTypeOptions(filtered);
    }
  }, [lov.instrumentTypeOptions, formData.productIdentity?.productCategory]);

  const toNumberIfIntegerString = (value) => {
    if (typeof value === "string" && /^\d+$/.test(value)) {
      return Number(value);
    }
    return value;
  };

  const getSecurityProductTemplate = () => {
    setIsLoading(true);

    const url = `${apiConfig.GetSecurityProducts}/template`;

    BOM.FetchReqAction({ session }, url, (err, res) => {
      setIsLoading(false);

      if (err) {
        BOM.AlertMsg("danger", err.defaultUserMessage);
        return;
      }

      console.log("PRODUCT CATEGORIES:", res?.productCategories);
      console.log("INSTRUMENT TYPES:", res?.instrumentTypes);

      console.log(res);

      setLov({
        // --- Product Identity ---
        productCategoryOptions:
          res?.productCategories?.map((item) => ({
            value: item.value,
            label: item.text,
            code: item.code,
          })) || [],

        instrumentTypeOptions:
          res?.instrumentTypes?.map((item) => ({
            value: item.id,
            label: item.name,
            code: item.name,
          })) || [],

        assetClassOptions:
          res?.assetClassOptions?.map((item) => ({
            value: item.value,
            label: item.text,
            code: item.code,
          })) || [],

        issuerTypeOptions:
          res?.issuerTypes?.map((item) => ({
            value: item.id,
            label: item.name,
            code: item.id,
          })) || [],

        currencyOptions:
          res?.currencies?.map((item) => ({
            value: item.code,
            label: item.displayLabel,
          })) || [],

        dayCountConventionOptions:
          res?.dayCountConventionOptions?.map((item) => ({
            value: item.value,
            label: item.text,
          })) || [],

        couponFrequencyOptions:
          res?.couponFrequencyOptions?.map((item) => ({
            value: item.value,
            label: item.text,
          })) || [],

        businessDayConventionOptions:
          res?.businessDayConventionOptions?.map((item) => ({
            value: item.value,
            label: item.text,
          })) || [],

        quotationTypeOptions:
          res?.quotationTypeOptions?.map((item) => ({
            value: item.value,
            label: item.text,
          })) || [],

        marketTypeOptions:
          res?.marketTypeOptions?.map((item) => ({
            value: item.value,
            label: item.text,
          })) || [],

        valuationApproachOptions:
          res?.valuationMethodOptions?.map((item) => ({
            value: item.value,
            label: item.text,
          })) || [],

        productCodeOptions: [
          ...(res?.bondProductOptions || []).map((item) => ({
            value: item.id,
            label: `${item.productName} (${item.productCode})`,
            productType: "BOND",
          })),
          ...(res?.treasuryBillProductOptions || []).map((item) => ({
            value: item.id,
            label: `${item.productName} (${item.productCode})`,
            productType: "TBILL",
          })),
        ],

        // --- EconomicBehaviour form options ---
        interestStructureOptions:
          res?.interestStructureOptions?.map((item) => ({
            value: item.id,
            label: item.name,
          })) || [],

        couponTypeOptions:
          res?.couponTypeOptions?.map((item) => ({
            value: item.id,
            label: item.name,
          })) || [],

        amortisationTypeOptions:
          res?.amortisationTypeOptions?.map((item) => ({
            value: item.id,
            label: item.name,
          })) || [],

        principalRepaymentTypeOptions:
          res?.principalRepaymentTypeOptions?.map((item) => ({
            value: item.id,
            label: item.name,
          })) || [],

        incomeRecognitionMethodOptions:
          res?.incomeRecognitionMethodOptions?.map((item) => ({
            value: item.id,
            label: item.name,
          })) || [],

        accrualPostingFrequencyOptions:
          res?.accrualPostingFrequencyOptions?.map((item) => ({
            value: item.value,
            label: item.text,
          })) || [],

        pricingMethods:
          res?.pricingMethods?.map((item) => ({
            value: item.value,
            label: item.text,
          })) || [],

        quotationTypeOptions:
          res?.quotationTypeOptions?.map((item) => ({
            value: item.id,
            label: item.name,
          })) || [],

        yieldBasisOptions:
          res?.yieldBasisOptions?.map((item) => ({
            value: item.id,
            label: item.name,
          })) || [],

        cleanDirtyPriceFlagOptions:
          res?.cleanDirtyPriceFlagOptions?.map((item) => ({
            value: item.id,
            label: item.name,
          })) || [],

        valuationCurveTypeOptions:
          res?.valuationCurveTypeOptions?.map((item) => ({
            value: item.id,
            label: item.name,
          })) || [],

        // DAY COUNT CALENDER
        businessDayAdjustmentOptions:
          res?.businessDayAdjustmentOptions?.map((item) => ({
            value: item.value,
            label: item.text,
          })) || [],

        holidayCalendarOptions:
          res?.holidayCalendarOptions?.map((item) => ({
            value: item.name,
            label: item.name,
            id: item.id,
          })) || [],

        settlementDateTypes:
          res?.settlementDateTypes?.map((item) => ({
            value: item.value,
            label: item.text,
          })) || [],

        // AccountingIFRSForm

        ifrsClassificationOptions:
          res?.ifrsClassificationOptions?.map((item) => ({
            value: item.value,
            label: item.text,
          })) || [],

        incomeGlAccounts:
          res?.incomeGlAccounts?.map((item) => ({
            value: item.id,
            label: `${item.name} - ${item.glCode}`,
          })) || [],

        assetGLAccounts:
          res?.assetGLAccounts?.map((item) => ({
            value: item.id,
            label: `${item.name} - ${item.glCode}`,
          })) || [],

        // RiskRegulatoryBehaviourForm
        durationMethodOptions:
          res?.durationMethodOptions?.map((item) => ({
            value: item.id,
            label: item.name,
          })) || [],

        exposureTypeOptions:
          res?.exposureTypeOptions?.map((item) => ({
            value: item.id,
            label: item.name,
          })) || [],

        creditRiskClassOptions:
          res?.creditRiskClassOptions?.map((item) => ({
            value: item.id,
            label: item.name,
          })) || [],

        accountingBasisOptions:
          res?.accountingBasisOptions?.map((item) => ({
            value: item.id,
            label: item.name,
          })) || [],

        // Life cycle Event Controls Form
        maturityEventCodeOptions:
          res?.maturityEventCodeOptions?.map((item) => ({
            value: item.id,
            label: item.name,
          })) || [],

        // Limits Eligibility Rules
        currencies:
          res?.currencies?.map((item) => ({
            value: item.name,
            label: item.displayLabel,
            code: item.code,
          })) || [],

        bondCouponFrequencies:
          res?.bondCouponFrequencies?.map((item) => ({
            value: item.value,
            label: item.text,
          })) || [],

        interestCalculationBasisOptions:
          res?.interestCalculationBasisOptions?.map((item) => ({
            value: item.value,
            label: item.text,
          })) || [],
      });
    });
  };

  const normalizeGL = (gl) => {
    if (!gl || !gl.debitAccount || !gl.creditAccount) return null;

    // Backend expects [debitId, creditId] array format
    return [gl.debitAccount, gl.creditAccount];
  };

  const fetchProductById = (id) => {
    setIsLoading(true);

    BOM.FetchReqAction(
      { session },
      `${apiConfig.GetSecurityProducts}/${id}`,
      (err, res) => {
        setIsLoading(false);
        if (err) {
          BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
          return;
        }

        // ---- filter instrument types based on the product category ----
        const categoryValue = res.productCategory?.value;
        const allowedCodes = categoryInstrumentMap[categoryValue] || [];
        const filtered = lov.instrumentTypeOptions.filter((opt) =>
          allowedCodes.includes(opt.code),
        );
        setFilteredInstrumentTypeOptions(filtered);
        // ---------------------------------------------------------------

        const normalized = {
          productIdentity: {
            productName: res.productName,
            productCode: res.productCode,
            productCategory: res.productCategory?.value,
            instrumentType: res.instrumentType?.id,
            assetClass: res.assetClass?.value,
            issuerType: res.issuerType?.id,
            currencyCode: res.currencyCode,
            tradabilityFlag: res.tradabilityFlag,
            activeFlag: res.activeFlag,
          },
          behaviourRules: {
            interestStructure: res.interestStructure?.value,
            couponType: res.couponType?.value,
            couponFrequency: res.couponFrequency,
            amortisationType: res.amortisationType?.value,
            principalRepaymentType: res.principalRepaymentType?.value,
            incomeRecognitionMethod: res.incomeRecognitionMethod?.value,
            accrualRequiredFlag: res.accrualRequiredFlag,
            accrualFrequency: res.accrualFrequency,
          },
          pricingLogic: {
            pricingMethod: res?.pricingMethod?.value,
            quotationType: res?.quotationType?.value,
            yieldBasis: res?.yieldBasis?.value,
            discountBasis:
              res.discountBasis !== undefined && res.discountBasis !== null
                ? Number(res.discountBasis)
                : undefined,
            priceDecimals: res?.priceDecimals,
            yieldDecimals: res?.yieldDecimals,
            cleanDirtyPriceFlag: res?.cleanDirtyPriceFlag?.value,
            valuationCurveType: res?.valuationCurveType?.value,
          },
          dayCountCalendarRules: {
            dayCountConvention: res.dayCountConvention?.value,
            businessDayConvention: res.businessDayConvention,
            holidayCalendarCode: toNumberIfIntegerString(
              res.holidayCalendarCode,
            ),
            settlementDays: res.settlementDays,
            maturityAdjustmentRule: res.maturityAdjustmentRule,
          },
          accountingIFRS: {
            ifrsClassification: res.ifrsClassification?.value,
            accountingBasis: res.accountingBasis?.value,
            revaluationRequiredFlag: res?.revaluationRequiredFlag,
            revaluationFrequency: res.revaluationFrequency?.value,
            principalGL: {
              debitAccount: res.principalGL?.debitAccount?.id,
              creditAccount: res.principalGL?.creditAccount?.id,
            },
            accruedInterestGL: {
              debitAccount: res.accruedInterestGL?.debitAccount?.id,
              creditAccount: res.accruedInterestGL?.creditAccount?.id,
            },
            interestIncomeGL: {
              debitAccount: res.interestIncomeGL?.debitAccount?.id,
              creditAccount: res.interestIncomeGL?.creditAccount?.id,
            },
            unrealisedIncomeGL: {
              debitAccount: res.unrealisedIncomeGL?.debitAccount?.id,
              creditAccount: res.unrealisedIncomeGL?.creditAccount?.id,
            },
          },
          riskRegulatoryBehaviour: {
            riskWeight: res?.riskWeight,
            durationMethod: res?.durationMethod?.value,
            exposureType: res?.exposureType?.value,
            eligibleForSlrFlag: res?.eligibleForSlrFlag,
            eligibleForLcrFlag: res?.eligibleForLcrFlag,
            haircutPercentage: res?.haircutPercentage,
            creditRiskClass: res?.creditRiskClass?.value,
          },
          lifeCycleAndEvents: {
            earlySaleAllowed: res?.earlyTerminationWithPenalty,
            rolloverAllowed: res?.allowRollover,
            autoMaturityFlag: res?.autoMaturityFlag,
            maturityEventCode: res?.maturityEventCode.value,
            penaltyApplicableFlag: res?.penaltyApplicableFlag,
          },
          limitsEligibilityRules: {
            counterpartyLimitRequired: res?.counterpartyLimitRequired,
            portfolioLimitRequired: res?.portfolioLimitRequired,
            dealSizeMin: res?.dealSizeMin,
            dealSizeMax: res?.dealSizeMax,
            currencyRestriction: res?.currencyRestriction,
            currencyCode: res?.currencyCode,
          },
        };

        setFormData(normalized);
        form.setFieldsValue({
          ...normalized.productIdentity,
          ...normalized.behaviourRules,
          ...normalized.pricingLogic,
          ...normalized.dayCountCalendarRules,
          ...normalized.riskRegulatoryBehaviour,
          ...normalized.lifeCycleAndEvents,
          ...normalized.limitsEligibilityRules,
          principalGL: normalized.accountingIFRS.principalGL,
          accruedInterestGL: normalized.accountingIFRS.accruedInterestGL,
          interestIncomeGL: normalized.accountingIFRS.interestIncomeGL,
          unrealisedIncomeGL: normalized.accountingIFRS.unrealisedIncomeGL,
          ifrsClassification: normalized.accountingIFRS.ifrsClassification,
          accountingBasis: normalized.accountingIFRS.accountingBasis,
          revaluationRequiredFlag:
            normalized.accountingIFRS.revaluationRequiredFlag,
          revaluationFrequency: normalized.accountingIFRS.revaluationFrequency,
        });
      },
    );
  };

  const handleProductCategoryChange = (categoryValue) => {
    const allowedCodes = categoryInstrumentMap[categoryValue] || [];
    const filtered = lov.instrumentTypeOptions.filter((opt) =>
      allowedCodes.includes(opt.code),
    );

    setFilteredInstrumentTypeOptions(filtered);
    form.setFieldValue("instrumentType", undefined);
  };

  const handleFinish = (key) => (values) => {
    setFormData((prev) => ({ ...prev, [key]: values }));
    setCurrent((prev) => prev + 1);
  };

  const nextButtonClick = () => form.submit();
  const prevButtonClick = () => setCurrent((prev) => prev - 1);
  const onCancel = () => navigate("/security-product");

  const onSubmit = () => {
    const payload = {
      ...formData.productIdentity,
      ...formData.behaviourRules,
      ...formData.pricingLogic,
      ...formData.dayCountCalendarRules,

      ...formData.accountingIFRS,
      principalGL: normalizeGL(formData.accountingIFRS.principalGL),
      accruedInterestGL: normalizeGL(formData.accountingIFRS.accruedInterestGL),
      interestIncomeGL: normalizeGL(formData.accountingIFRS.interestIncomeGL),
      unrealisedIncomeGL: normalizeGL(
        formData.accountingIFRS.unrealisedIncomeGL,
      ),

      ...formData.riskRegulatoryBehaviour,
      ...formData.lifeCycleAndEvents,
      ...formData.limitsEligibilityRules,
    };

    const url = isEditMode
      ? `${apiConfig.GetSecurityProducts}/${selectedProductId}`
      : apiConfig.GetSecurityProducts;

    const body = {
      ...payload,
      session,
      method: isEditMode ? "PUT" : "POST",
      locale: "en",
    };

    setIsLoading(true);

    BOM.FetchReqAction(
      body,
      url,
      (err) => {
        setIsLoading(false);

        if (err) {
          BOM.AlertMsg(cnt.DANGER, err.errors?.[0]?.defaultUserMessage);
          return;
        }
        BOM.AlertMsg(
          cnt.SUCCESS,
          isEditMode
            ? "Security Product Updated Successfully"
            : "Security Product Created Successfully",
        );
        navigate("/security-product");
      },
      {
        navigate,
        showCheckerModal: showCheckerApprovalModal,
        onCheckerApproval: (res) => {
          navigate("/security-product", {
            state: {
              id: res.resourceId,
            },
          });
          setIsLoading(false);
        },
      },
    );
  };

  console.log(formData.productIdentity);
  const forms = [
    <ProductIdentityForm
      form={form}
      initialValues={formData.productIdentity}
      onFinish={handleFinish("productIdentity")}
      productCategoryOptions={lov.productCategoryOptions}
      instrumentTypeOptions={filteredInstrumentTypeOptions}
      assetClassOptions={lov.assetClassOptions}
      productStatusOptions={productStatusOptions}
      issuerTypeOptions={lov.issuerTypeOptions}
      onProductCategoryChange={handleProductCategoryChange}
      productCategory={formData.productIdentity?.productCategory}
    />,
    <EconomicBehaviour
      key="behaviour-rules"
      form={form}
      initialValues={formData.behaviourRules}
      onFinish={handleFinish("behaviourRules")}
      // Options coming from template (lov)
      interestStructureOptions={lov.interestStructureOptions || []}
      couponTypeOptions={lov.couponTypeOptions || []}
      couponFrequencyOptions={lov.couponFrequencyOptions || []}
      amortisationTypeOptions={lov.amortisationTypeOptions || []}
      principalRepaymentTypeOptions={lov.principalRepaymentTypeOptions || []}
      incomeRecognitionMethodOptions={lov.incomeRecognitionMethodOptions || []}
      accrualPostingFrequencyOptions={lov.accrualPostingFrequencyOptions || []}
      // Static options
      yesNoOptions={yesNoOptions}
      bondCouponFrequencies={lov.bondCouponFrequencies}
      productCategory={formData.productIdentity?.productCategory}
    />,
    <PricingYieldConfigurationForm
      key="pricing-logic"
      form={form}
      initialValues={formData.pricingLogic}
      onFinish={handleFinish("pricingLogic")}
      quotationTypeOptions={lov.quotationTypeOptions}
      pricingFrequencyOptions={lov.pricingMethods}
      dayCountConventionOptions={lov.dayCountConventionOptions}
      cleanDirtyPriceFlagOptions={lov.cleanDirtyPriceFlagOptions}
      pricingMethods={lov.pricingMethods}
      yieldBasisOptions={lov.yieldBasisOptions}
      valuationCurveTypeOptions={lov.valuationCurveTypeOptions}
      bondCouponFrequencies={lov.bondCouponFrequencies}
      productCategory={formData.productIdentity?.productCategory}
    />,

    <DayCountCalendarRulesForm
      key="day-count-calendar"
      form={form}
      initialValues={formData.dayCountCalendarRules}
      onFinish={handleFinish("dayCountCalendarRules")}
      dayCountConventionOptions={lov.dayCountConventionOptions}
      businessDayAdjustmentOptions={lov.businessDayAdjustmentOptions}
      holidayCalendarOptions={lov.holidayCalendarOptions}
      settlementDateTypes={lov.settlementDateTypes}
      interestCalculationBasisOptions={lov.interestCalculationBasisOptions}
      maturityAdjustmentRule={maturityAdjustmentRule}
    />,
    <AccountingIFRSForm
      key="accounting-ifrs"
      form={form}
      initialValues={formData.accountingIFRS}
      onFinish={handleFinish("accountingIFRS")}
      ifrsClassificationOptions={lov.ifrsClassificationOptions}
      valuationMethodOptions={lov.valuationMethodOptions}
      gLMapping={lov.incomeGlAccounts}
      assetGL={lov.assetGLAccounts}
      accrualPostingFrequencyOptions={lov.accrualPostingFrequencyOptions}
      defaultCashOptions={[{ value: "DEFINED", label: "Defined" }]}
      accountingBasisOptions={lov.accountingBasisOptions}
      productCategory={formData.productIdentity?.productCategory}
    />,
    <RiskRegulatoryBehaviourForm
      key="risk-regulatory"
      form={form}
      initialValues={formData.riskRegulatoryBehaviour}
      onFinish={handleFinish("riskRegulatoryBehaviour")}
      durationMethodOptions={lov.durationMethodOptions}
      creditRiskClassOptions={lov.creditRiskClassOptions}
      exposureTypeOptions={lov.exposureTypeOptions}
      productCategory={formData.productIdentity?.productCategory}
    />,
    <LifecycleEventControlsForm
      key="settlement-operations"
      form={form}
      initialValues={formData.settlementOperations}
      onFinish={handleFinish("lifeCycleAndEvents")}
      settlementCycleOptions={lov.settlementCycleOptions}
      settlementMethodOptions={lov.settlementMethodOptions}
      defaultCashOptions={[{ value: "DEFINED", label: "Defined" }]}
      businessDayAdjustmentOptions={lov.businessDayAdjustmentOptions}
      holidayCalendarOptions={lov.holidayCalendarOptions}
      yesNoOptions={yesNoOptions}
      maturityEventOptions={lov.maturityEventCodeOptions}
      productCategory={formData.productIdentity?.productCategory}
    />,
    <LimitsEligibilityRules
      key="limits-eligibility"
      form={form}
      initialValues={formData.chargesTaxConfiguration}
      onFinish={handleFinish("limitsEligibilityRules")}
      yesNoOptions={yesNoOptions}
      currencyOptions={lov.currencies}
      productCategory={formData.productIdentity?.productCategory}
    />,
    <SecurityProductPreview
      key="preview"
      formData={formData} // full nested formData
      productCategoryOptions={lov.productCategoryOptions}
      instrumentTypeOptions={lov.instrumentTypeOptions}
      assetClassOptions={lov.assetClassOptions}
      yesNoOptions={yesNoOptions}
      quotationTypeOptions={lov.quotationTypeOptions}
      dayCountConventionOptions={lov.dayCountConventionOptions}
      holidayCalendarOptions={lov.holidayCalendarOptions}
      ifrsClassificationOptions={lov.ifrsClassificationOptions}
      currencyOptions={lov.currencies}
      bondCouponFrequencies={lov.bondCouponFrequencies}
      interestStructureOptions={lov.interestStructureOptions}
      couponTypeOptions={lov.couponTypeOptions}
      couponFrequencyOptions={lov.couponFrequencyOptions}
      accrualPostingFrequencyOptions={lov.accrualPostingFrequencyOptions}
      incomeGlAccounts={lov.incomeGlAccounts}
      assetGLAccounts={lov.assetGLAccounts}
      accountingBasisOptions={lov.accountingBasisOptions}
      pricingMethods={lov.pricingMethods}
      principalRepaymentTypeOptions={lov.principalRepaymentTypeOptions || []}
      incomeRecognitionMethodOptions={lov.incomeRecognitionMethodOptions || []}
      yieldBasisOptions={lov.yieldBasisOptions}
      amortisationTypeOptions={lov.amortisationTypeOptions || []}
      cleanDirtyPriceFlagOptions={lov.cleanDirtyPriceFlagOptions}
      valuationCurveTypeOptions={lov.valuationCurveTypeOptions}
      businessDayAdjustmentOptions={lov.businessDayAdjustmentOptions}
      settlementDateTypes={lov.settlementDateTypes}
      interestCalculationBasisOptions={lov.interestCalculationBasisOptions}
      maturityAdjustmentRule={maturityAdjustmentRule}
      exposureTypeOptions={lov.exposureTypeOptions}
      creditRiskClassOptions={lov.creditRiskClassOptions}
      durationMethodOptions={lov.durationMethodOptions}
      maturityEventOptions={lov.maturityEventCodeOptions}
    />,
  ];

  const isLastStep = current === forms.length - 1;

  return (
    <MainLayout
      pageItems={
        isEditMode
          ? staticConfig.editSecurityProduct
          : staticConfig.newSecurityProduct
      }
      onSave={onSubmit}
    >
      <TopContent
        pageTitle={
          isEditMode
            ? labelConfig.editSecurityProduct
            : labelConfig.securityProduct
        }
        createPageTitle={
          isEditMode
            ? labelConfig.editSecurityProduct
            : labelConfig.addSecurityProduct
        }
      />

      <Spin spinning={isLoading} size="large">
        <div
          style={{
            display: "flex",
            paddingLeft: 44,
            marginTop: 20,
            width: "100%",
          }}
        >
          {/* Navigation */}
          <div style={{ width: 288, padding: "20px 0" }}>
            <Steps
              className="custom-step-style"
              style={{ height: "723px" }}
              current={current}
              direction="vertical"
              items={stepTitles.map((title) => ({
                title: <Text strong>{title}</Text>,
                disabled: true,
              }))}
            />
          </div>

          {/* Form */}
          <Card
            style={{
              borderRadius: 20,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              padding: "0 20px",
              height: `${isLastStep ? "723px" : "100%"}`,
              overflowY: `${isLastStep ? "auto" : "hidden"}`,
            }}
          >
            <div style={{ flexGrow: 1 }}>{forms[current]}</div>

            {/* Buttons */}
            <div
              style={{
                paddingTop: 30,
                display: "flex",
                justifyContent: "flex-end",
                gap: 16,
              }}
            >
              {current > 0 && (
                <Button
                  onClick={prevButtonClick}
                  style={{
                    borderRadius: 8,
                    padding: "20px 15px",
                    fontSize: "16px",
                    fontWeight: "600",
                  }}
                >
                  Previous
                </Button>
              )}
              {!isLastStep && (
                <Button
                  type="primary"
                  onClick={nextButtonClick}
                  style={{
                    backgroundColor: "#15CCAE",
                    color: "white",
                    border: "1px solid #BA24D5",
                    borderRadius: 8,
                    padding: "20px 15px",
                    fontSize: "16px",
                    fontWeight: "600",
                  }}
                >
                  Next
                </Button>
              )}
              {isLastStep && (
                <Button
                  type="primary"
                  onClick={onSubmit}
                  style={{
                    backgroundColor: "#15CCAE",
                    color: "white",
                    border: "1px solid #BA24D5",
                    borderRadius: 8,
                    padding: "20px 15px",
                    fontSize: "16px",
                    fontWeight: "600",
                  }}
                >
                  {isEditMode ? "Edit Security Product" : "Create Product"}
                </Button>
              )}
            </div>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps)(AddSecurityProduct);
